import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import * as React from 'react'

import { cn } from '@/lib/ui/style-helpers'

import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/lemmala-design-system/command/command'
import { Input } from '@/components/lemmala-design-system/input/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/lemmala-design-system/popover/popover'

import { CMDK_NO_HIGHLIGHT } from './consts'
import { DropdownSelectContext, DropdownSelectContextValue } from './dropdown-select-context'
import { DropdownSelectItemComponent } from './dropdown-select-item'
import type { DropdownSelectItem } from './dropdown-select.types'
import { DropdownSelectProps } from './dropdown-select.types'
import { SortableDropdownSelectItem } from './sortable-dropdown-select-item'
import { getPartiallySelectedIds, indexItemTree, listItems, toggleItem } from './utils'

export const DropdownSelect = React.forwardRef<HTMLDivElement, DropdownSelectProps>(
  (
    {
      items,
      multi = false,
      draggable = false,
      search = false,
      selectedItems = [],
      onItemSelect,
      onMultiValueChange,
      onReorder,
      trigger,
      headerContent,
      footerContent,
      footerClassName,
      searchPlaceholder = 'Search...',
      open: controlledOpen,
      onOpenChange: controlledOnOpenChange,
      popoverAlign = 'start',
      className,
      isModal = false,
      showTooltips = false,
    },
    ref,
  ) => {
    const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false)
    const [searchTerm, setSearchTerm] = React.useState('')
    const [localItems, setLocalItems] = React.useState<DropdownSelectItem[] | null>(null)
    const [commandValue, setCommandValue] = React.useState(CMDK_NO_HIGHLIGHT)

    const open = controlledOpen ?? uncontrolledOpen
    const setOpen = controlledOnOpenChange ?? setUncontrolledOpen

    const resetCommandHighlight = React.useCallback(() => {
      setCommandValue(CMDK_NO_HIGHLIGHT)
    }, [])

    React.useLayoutEffect(() => {
      if (open) {
        // oxlint-disable-next-line react/set-state-in-effect
        resetCommandHighlight()
      }
    }, [open, resetCommandHighlight])

    const handleItemSelect = React.useCallback(
      (itemId: string) => {
        if (!multi && onItemSelect) {
          onItemSelect(itemId)
          setOpen(false)
        }
      },
      [multi, onItemSelect, setOpen],
    )

    const shouldManageLocal = React.useMemo(() => draggable && !onReorder, [draggable, onReorder])

    React.useEffect(() => {
      if (shouldManageLocal) {
        // oxlint-disable-next-line react/set-state-in-effect
        setLocalItems(items)
      }
    }, [items, shouldManageLocal])

    const effectiveItems = React.useMemo(
      () => (draggable ? (localItems ?? items) : items),
      [draggable, items, localItems],
    )

    const itemTree = React.useMemo(() => indexItemTree(effectiveItems), [effectiveItems])

    const partiallySelectedItems = React.useMemo(
      () => getPartiallySelectedIds(selectedItems, itemTree),
      [selectedItems, itemTree],
    )

    const handleItemToggle = React.useCallback(
      (itemId: string, checked: boolean) => {
        if (multi && onMultiValueChange) {
          onMultiValueChange(toggleItem(selectedItems, itemId, checked, itemTree))
        }
      },
      [multi, selectedItems, itemTree, onMultiValueChange],
    )

    const filteredItems = React.useMemo(() => {
      const listedItems = listItems(effectiveItems)
      if (!search || !searchTerm) return listedItems
      return listedItems.filter(({ item }) =>
        item.label.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    }, [effectiveItems, search, searchTerm])

    const sensors = useSensors(
      useSensor(PointerSensor, {
        activationConstraint: { distance: 2, tolerance: 5 },
      }),
    )

    const handleDragEnd = React.useCallback(
      (event: DragEndEvent) => {
        const { active, over } = event
        if (!over || active.id === over.id) {
          return
        }

        const source = shouldManageLocal ? effectiveItems : items
        const oldIndex = source.findIndex((i) => i.id === active.id)
        const newIndex = source.findIndex((i) => i.id === over.id)
        if (oldIndex === -1 || newIndex === -1) return

        const updated = [...source]
        const [moved] = updated.splice(oldIndex, 1)
        updated.splice(newIndex, 0, moved)

        if (onReorder) onReorder(updated)
        if (shouldManageLocal) setLocalItems(updated)
      },
      [effectiveItems, items, onReorder, shouldManageLocal],
    )

    const contextValue: DropdownSelectContextValue = React.useMemo(
      () => ({
        multi,
        draggable,
        selectedItems,
        partiallySelectedItems,
        onItemSelect: handleItemSelect,
        onItemToggle: handleItemToggle,
        showTooltips,
      }),
      [
        multi,
        draggable,
        selectedItems,
        partiallySelectedItems,
        handleItemSelect,
        handleItemToggle,
        showTooltips,
      ],
    )

    return (
      <DropdownSelectContext.Provider value={contextValue}>
        <Popover open={open} onOpenChange={setOpen} modal={isModal}>
          <PopoverTrigger asChild>{trigger}</PopoverTrigger>
          <PopoverContent
            ref={ref}
            className={cn(
              'z-max w-fit min-w-44 max-w-80 rounded border-palette-zinc-200 p-1',
              popoverAlign === 'center' &&
                'min-w-[var(--radix-popover-trigger-width)] max-w-none [&_span.truncate]:max-w-none',
              className,
            )}
            align={popoverAlign}
            onOpenAutoFocus={(e) => e.preventDefault()}
            onPointerLeave={(e) => {
              const related = e.relatedTarget
              if (related instanceof Node && e.currentTarget.contains(related)) {
                return
              }
              resetCommandHighlight()
            }}
          >
            <Command value={commandValue} onValueChange={setCommandValue}>
              {headerContent && (
                <div className='px-2 pb-2 pt-1 text-main-foreground-primary text-label-default'>
                  {headerContent}
                </div>
              )}

              {search && (
                <Input
                  className='mb-2'
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              )}

              <CommandList>
                <CommandGroup className='max-h-64 overflow-y-auto p-0'>
                  <CommandItem
                    value={CMDK_NO_HIGHLIGHT}
                    className='hidden h-0 min-h-0 overflow-hidden p-0'
                    aria-hidden
                  />
                  {filteredItems.length === 0 ? (
                    <div className='px-2 py-4 text-center text-main-foreground-tertiary text-body-small'>
                      No values available
                    </div>
                  ) : draggable ? (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={filteredItems.map(({ item }) => item.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        {filteredItems.map(({ item }) => (
                          <SortableDropdownSelectItem key={item.id} item={item} />
                        ))}
                      </SortableContext>
                    </DndContext>
                  ) : (
                    filteredItems.map(({ item, depth }) => (
                      <DropdownSelectItemComponent key={item.id} item={item} depth={depth} />
                    ))
                  )}
                </CommandGroup>
              </CommandList>
            </Command>

            {footerContent && (
              <div
                className={cn(
                  'border-t border-main-border-default px-2 pb-1 pt-2',
                  footerClassName,
                )}
              >
                {footerContent}
              </div>
            )}
          </PopoverContent>
        </Popover>
      </DropdownSelectContext.Provider>
    )
  },
)
DropdownSelect.displayName = 'DropdownSelect'
